<script setup>
/**
 * The one dropdown in the Waslo tree, and it exists because of a rendering bug that was
 * visible in the very first screenshot of the connection editor.
 *
 * A bare `<select>` inherits Chatwoot's global form styling, which paints its own arrow as
 * a background image positioned for a control with different padding: it landed as a small
 * triangle in the TOP-START CORNER of the box, overlapping the label line, with the value
 * sitting low underneath it. Nothing in our markup put it there and nothing in our markup
 * could move it. `appearance-none` + `bg-none` removes the native and the inherited arrow
 * together, and the chevron below is ours — an icon coloured by an `n-*` class, which is
 * colour the theming contract can actually read (§5 R2).
 *
 * THE CHEVRON IS PINNED WITH `end-3`, NOT WITH AN `ltr:`/`rtl:` PAIR, and that distinction
 * cost one screenshot. Tailwind's `ltr:` variant compiles to `[dir="ltr"] &` — an attribute
 * selector — so on any document that does not set `dir` explicitly NEITHER half of the pair
 * matches, the absolute box falls back to its static position, and the chevron rendered on
 * the WRONG SIDE of the value ("⌄GET"). `end-3` is `inset-inline-end`, a real logical
 * property resolved from the computed direction with no attribute to depend on. R6's
 * escape hatch is for the cases where no logical property exists; this is not one of them
 * (Tailwind 3.4, and `components-next/sidebar` already uses `start-0`).
 *
 * The control carries no user-typed text — its options are fixed machine values with
 * translated labels — so it is not an R7 concern, and the popup itself follows the theme
 * because `color-scheme` is already on the host document.
 */
import { computed, getCurrentInstance } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  /** `[{ value, label, disabled? }]` — the label is already translated by the caller. */
  options: { type: Array, default: () => [] },
  /**
   * "Nothing chosen yet", as a real disabled first option.
   *
   * Without one a `<select>` whose model is `''` renders its FIRST option — so an untouched
   * control claims a value nobody picked, or, when the option list is not loaded yet, an
   * empty box that reads as broken. Both were on screen before this existed.
   */
  placeholder: { type: String, default: '' },
  /** One option in the list that exists but cannot be chosen (see `disabled` below). */
  disabled: { type: Boolean, default: false },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);

const fieldId = `waslo-select-${getCurrentInstance().uid}`;

// The same rule `WasloInput` writes down: the TYPE alone is not enough. A caller that
// declares "when this field has something to say it is an error" and has nothing to say yet
// is the ordinary resting case, and reading the type alone paints an untouched control ruby
// on first paint.
const hasError = computed(
  () => props.messageType === 'error' && Boolean(props.message)
);
</script>

<template>
  <div class="flex flex-col min-w-0">
    <label
      v-if="label"
      :for="fieldId"
      class="mb-1 text-sm font-medium text-n-slate-12"
    >
      {{ label }}
    </label>
    <div class="relative">
      <select
        :id="fieldId"
        :value="modelValue"
        :disabled="disabled"
        :class="
          hasError
            ? 'outline-n-ruby-8 hover:outline-n-ruby-9'
            : 'outline-n-slate-7 hover:outline-n-slate-8'
        "
        class="block w-full h-10 !py-0 !ps-3 !pe-9 !mb-0 text-sm bg-none appearance-none border-none border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50"
        v-bind="$attrs"
        @change="emit('update:modelValue', $event.target.value)"
      >
        <!-- Disabled, so it can be SHOWN but never re-selected once a real choice is made. -->
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
      <span
        class="absolute -translate-y-1/2 pointer-events-none top-1/2 end-3 size-4 i-lucide-chevron-down text-n-slate-11"
        aria-hidden="true"
      />
    </div>
    <p
      v-if="message"
      class="min-w-0 mt-2 mb-0 text-sm"
      :class="hasError ? 'text-n-ruby-9' : 'text-n-slate-11'"
    >
      {{ message }}
    </p>
  </div>
</template>
